import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { testimonials } from '../data/content';

// Google "G" logo (official multi-color)
const GoogleG = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// German first names for realistic Google reviewer avatars
const avatarBg = ['#1E88E5', '#43A047', '#E53935', '#8E24AA', '#F4511E', '#00897B', '#FB8C00', '#3949AB', '#6D4C41'];

const reviewers = [
  { name: 'Michael Weber', when: 'vor 2 Wochen' },
  { name: 'Sabine Krüger', when: 'vor 1 Monat' },
  { name: 'Familie Hoffmann', when: 'vor 3 Wochen' },
  { name: 'Thomas Berger', when: 'vor 2 Monaten' },
  { name: 'Anke Schneider', when: 'vor 5 Tagen' },
  { name: 'Jens Petersen', when: 'vor 1 Monat' },
];

const initials = (n) => n.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const pages = Math.ceil(testimonials.length / 3);

  useEffect(() => {
    const id = setInterval(() => setIdx((v) => (v + 1) % pages), 7000);
    return () => clearInterval(id);
  }, [pages]);

  const go = (n) => setIdx(((n % pages) + pages) % pages);

  return (
    <section className="py-20 md:py-28 bg-[#FAF7F2] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="crown-divider text-[#B8892A]">Bewertungen</span>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0B1E44] leading-tight">
              Das sagen Kunden über <span className="text-[#DC2626]">Die Haushelden</span>.
            </h2>
            <div className="mt-6 inline-flex items-center gap-4 bg-white rounded-2xl border border-slate-200 px-5 py-3.5 card-shadow">
              <GoogleG className="w-8 h-8" />
              <div className="leading-tight">
                <div className="text-[13px] font-semibold text-slate-500">Google Bewertungen</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xl font-extrabold text-[#0B1E44]">4,9</span>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((s) => <Star key={s} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />)}
                  </div>
                  <span className="text-xs text-slate-500">· 47 Rezensionen</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a href="https://www.google.com/search?q=Die+Haushelden+Rethwisch" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-200 bg-white text-[#0B1E44] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors text-sm font-semibold">
              <GoogleG className="w-4 h-4" /> Auf Google ansehen <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => go(idx - 1)} className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:bg-[#0B1E44] hover:text-white hover:border-[#0B1E44] transition-colors flex items-center justify-center" aria-label="Zurück">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => go(idx + 1)} className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:bg-[#0B1E44] hover:text-white hover:border-[#0B1E44] transition-colors flex items-center justify-center" aria-label="Weiter">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-hidden">
          <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
            {Array.from({ length: pages }).map((_, pageIdx) => (
              <div key={pageIdx} className="min-w-full grid md:grid-cols-3 gap-6">
                {[0, 1, 2].map((o) => {
                  const i = (pageIdx * 3 + o) % testimonials.length;
                  const t = testimonials[i];
                  const r = reviewers[i % reviewers.length];
                  const bg = avatarBg[i % avatarBg.length];
                  return (
                    <article key={o} className="bg-white rounded-2xl p-6 md:p-7 card-shadow border border-slate-100 flex flex-col">
                      <header className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: bg }}>
                          {initials(r.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0B1E44] truncate">{r.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1.5">
                            <GoogleG className="w-3 h-3" /> Google · {r.when}
                          </div>
                        </div>
                      </header>
                      <div className="flex items-center gap-0.5 mt-4">
                        {[1,2,3,4,5].map((s) => <Star key={s} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />)}
                      </div>
                      <p className="mt-3 text-slate-700 leading-relaxed text-[15px] flex-1">{t.text}</p>
                      <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
                        Verifizierte Bewertung · {t.author}
                      </div>
                    </article>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} onClick={() => go(i)} className={`h-2 rounded-full transition-all ${i === idx ? 'w-8 bg-[#4285F4]' : 'w-2 bg-slate-300 hover:bg-slate-400'}`} aria-label={`Seite ${i + 1}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
